import * as rimApi from '../../utils/api/rimsApi';
import * as selectors from './selectors';

import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { changeView, fetchRim, fetchRimError, fetchRimList, fetchRimSuccess, fetchRimTypeCodesError, fetchRimTypeCodesSuccess, fetchRimsTotalError, fetchRimsTotalSuccess, setRimId, setRimLines, setRimLocked, setTabAsSaved, updateSigning, updateRimIBAN } from './actions';

import { DetailsRimActionTypes } from './types';
import { RimViews } from './model';
import { validateRimList } from '../overview/actions';
import { SagaIterator } from 'redux-saga';

function* handleFetchRimList(action: ReturnType<typeof fetchRimList>): SagaIterator {
  try {
    const [rimListResponse, rimTypeCodes] = yield all([
      call(rimApi.getRimsTotal, action.payload.instructionId),
      call(rimApi.getRimTypeCodes)
    ]);

    if (!rimListResponse.data) {
      yield put(fetchRimsTotalError(JSON.stringify(rimListResponse.response)));
    } else if (!rimTypeCodes.data) {
      yield put(fetchRimTypeCodesError(JSON.stringify(rimTypeCodes.response)));
    } else {
      yield put(fetchRimsTotalSuccess(rimListResponse.data));
      yield put(fetchRimTypeCodesSuccess(rimTypeCodes.data));

      let rimHeaderId = yield select(selectors.rimHeaderId);
      if (rimListResponse.data.rimsTotal.length > 0 && rimHeaderId === -1) {
        rimHeaderId = rimListResponse.data.rimsTotal[0].rimHeaderId;
        yield put(setRimId(rimHeaderId));
        yield put(fetchRim(rimHeaderId));
      }
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchRimsTotalError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchRimsTotalError('An unknown error occured.'));
    }
  }
}

function* handleFetchRim(action: ReturnType<typeof fetchRim>): SagaIterator {
  try {
    const res = yield call(rimApi.getRim, action.payload.rimHeaderId);

    if (!res.data) {
      yield put(fetchRimError(JSON.stringify(res.response)));
    } else {
      yield put(fetchRimSuccess(res.data));
      yield put(updateRimIBAN());
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchRimError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchRimError('An unknown error occured.'));
    }
  }
}

function* handleChangeView(action: ReturnType<typeof changeView>): SagaIterator {
  const currentView = yield select(selectors.previousView);
  const savingBlocked = yield select(selectors.caseBlocked);
  const instructionId = yield select(selectors.instructionId);

  if (currentView === action.payload.view) { return; }

  let rimHeaderId = yield select(selectors.rimHeaderId);
  let result: any = {};

  switch (currentView) {
    case RimViews.valueAndCause: {
      const isDirty = yield select(selectors.rimTabValueAndCauseIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.valueAndCause);
      if (!savingBlocked) {
         result = yield call(rimApi.saveValueAndCause, rimHeaderId, instructionId, data);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.vatAndPayment: {
      const isDirty = yield select(selectors.rimTabVatAndPaymentIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.vatAndPayment);
      if (!savingBlocked) {
        result = yield call(rimApi.saveVatAndPayment, rimHeaderId, instructionId, data);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.rimLines: {
      const isDirty = yield select(selectors.rimTabLinesIsDirty);
      if (!isDirty) {
        break;
      }
      const rimLines = yield select(selectors.rimLines);
      const vatAndPayment = yield select(selectors.vatAndPayment);
      if (!savingBlocked) {
        result = yield call(rimApi.saveRimLines, rimHeaderId, instructionId, rimLines, vatAndPayment.vat6ToBeJustifiedAmount, vatAndPayment.vat21ToBeJustifiedAmount);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.rimHeaderId;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setRimLines(result.data));
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.counterExpertise: {
      const isDirty = yield select(selectors.rimTabCounterExpertiseIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.counterExpertise);
      if (!savingBlocked) {
        result = yield call(rimApi.saveCounterExpertise, rimHeaderId, instructionId, data);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.communication: {
      const isDirty = yield select(selectors.rimTabCommunicationIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.communication);
      if (!savingBlocked) {
        result = yield call(rimApi.saveCommunication, rimHeaderId, instructionId, data);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.remark: {
      const isDirty = yield select(selectors.rimTabRemarkIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.remark);
      if (!savingBlocked) {
        result = yield call(rimApi.saveRemark, rimHeaderId, instructionId, data);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
    case RimViews.signing: {
      const isDirty = yield select(selectors.rimTabSigningIsDirty);
      if (!isDirty) { break; }

      const data = yield select(selectors.signing);
      const locked = yield select(selectors.locked);
      if (!savingBlocked) {
        result = yield call(rimApi.saveSigning, rimHeaderId, instructionId, data, locked);
      }
      if (!rimHeaderId) {
        rimHeaderId = result.data.id;
        yield put(fetchRim(rimHeaderId));
      }
      yield put(setTabAsSaved(currentView));
      break;
    }
  }

  yield put(setRimId(rimHeaderId));
  yield put(fetchRimList(instructionId));
}

function* handleUpdateSigning(action: ReturnType<typeof updateSigning>): SagaIterator {
  const rimId = yield select(selectors.rimHeaderId);
  const instructionId = yield select(selectors.instructionId);
  const savingBlocked = yield select(selectors.caseBlocked);
  let result: any = {};
  let newId: number = rimId;

  const data = yield select(selectors.signing);

  if (!savingBlocked) {
    result = yield call(rimApi.saveSigning, rimId, instructionId, data, true);
    newId = result.data.id;
  }

  yield put(setRimLocked());
  yield put(setRimId(newId));

  const existingRimTotal = yield select(selectors.rimsTotal, newId);

  if (!existingRimTotal) {
    yield put(fetchRimList(instructionId));
  }
}

function* handleGetRimListSuccess() {
  const rimIds: number[] = yield select(selectors.rimIds);
  yield put(validateRimList(rimIds));
}

function* watchDetailsRim() {
  yield takeEvery(DetailsRimActionTypes.FETCH_RIM_LIST, handleFetchRimList);
  yield takeEvery(DetailsRimActionTypes.FETCH_RIM, handleFetchRim);
  yield takeEvery(DetailsRimActionTypes.CHANGE_VIEW, handleChangeView);
  yield takeEvery(DetailsRimActionTypes.UPDATE_SIGNING, handleUpdateSigning);

  yield takeEvery(DetailsRimActionTypes.FETCH_RIMS_TOTAL_SUCCESS, handleGetRimListSuccess);
}

function* detailsRimSaga() {
  yield all([fork(watchDetailsRim)]);
}

export default detailsRimSaga;
